/**
 * RFC-149: MarketingCTA — single sanctioned wrapper for every marketing
 * call-to-action that targets the app (internal /auth* or external app
 * domains).
 *
 * Responsibilities:
 *  - Append UTM params to absolute (external) hrefs.
 *  - Persist UTM attribution to sessionStorage on click for internal /auth*
 *    targets so the signup handler can attribute the new account.
 *  - Render either a <Link> (internal SPA route) or an <a> (external),
 *    wrapping a Button styled per the marketing variant.
 *
 * Contract test (`src/__tests__/rfc149-marketing-cta.contracts.test.ts`)
 * enforces that every link/button matching this scope across the eight
 * marketing pages is rendered via this component.
 */

import { forwardRef, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, type ButtonProps } from '@/components/ui/button';
import { persistAttribution, withUtm, type UtmParams } from './withUtm';

interface BaseProps extends UtmParams {
  children: ReactNode;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
  ariaLabel?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

interface InternalProps extends BaseProps {
  /** SPA route, e.g. '/auth?signup=true'. Must start with '/'. */
  to: string;
  href?: never;
  external?: false;
}

interface ExternalProps extends BaseProps {
  /** Absolute URL (https://...). Opens in same tab unless `target` provided. */
  href: string;
  to?: never;
  external?: true;
  target?: string;
  rel?: string;
}

export type MarketingCTAProps = InternalProps | ExternalProps;

export const MarketingCTA = forwardRef<HTMLAnchorElement, MarketingCTAProps>(
  function MarketingCTA(props, ref) {
    const { medium, campaign, children, variant = 'hero', size = 'lg', className, ariaLabel, onClick } = props;

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      // Persist attribution for internal navigations; for external we also
      // persist so signup attribution survives a bounce back to /auth.
      persistAttribution({ medium, campaign });
      onClick?.(e);
    };

    if ('href' in props && props.href) {
      const ext = props as ExternalProps;
      const finalHref = withUtm(ext.href, { medium, campaign });
      return (
        <a
          ref={ref}
          href={finalHref}
          target={ext.target}
          rel={ext.rel ?? (ext.target === '_blank' ? 'noopener noreferrer' : undefined)}
          aria-label={ariaLabel}
          onClick={handleClick}
          className="inline-block"
          data-marketing-cta="external"
        >
          <Button variant={variant} size={size} className={className} asChild>
            <span>{children}</span>
          </Button>
        </a>
      );
    }

    // Internal SPA route — Link. Attribution is carried via sessionStorage,
    // not query string, because react-router strips unknown params on SPA
    // transitions and we don't want UTM-laden URLs cluttering the in-app
    // history.
    return (
      <Link
        ref={ref}
        to={props.to}
        aria-label={ariaLabel}
        onClick={handleClick}
        className="inline-block"
        data-marketing-cta="internal"
      >
        <Button variant={variant} size={size} className={className} asChild>
          <span>{children}</span>
        </Button>
      </Link>
    );
  }
);
